import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MENU_ITEMS = [
  { key: 'home', label: 'Home' },
  { key: 'schedule', label: 'Schedule' },
  { key: 'shows', label: 'Shows' },
  { key: 'catchup', label: 'Catch Up' },
  { key: 'dedications', label: 'Dedications' },
  { key: 'submit', label: 'Submit Music' },
];

const PUB_ID_RE = /^ca-pub-\d{16}$/;

export default function AdminSettings() {
  const [items, setItems] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const [adsEnabled, setAdsEnabled] = useState(false);
  const [publisherId, setPublisherId] = useState('');
  const [homeSlot, setHomeSlot] = useState('');
  const [scheduleSlot, setScheduleSlot] = useState('');
  const [showsSlot, setShowsSlot] = useState('');
  const [footerSlot, setFooterSlot] = useState('');
  const [adsAfricaDisabled, setAdsAfricaDisabled] = useState(false);
  const [sponsorEnabled, setSponsorEnabled] = useState(false);
  const [sponsorTitle, setSponsorTitle] = useState('');
  const [sponsorMessage, setSponsorMessage] = useState('');
  const [sponsorCtaLabel, setSponsorCtaLabel] = useState('');
  const [sponsorCtaUrl, setSponsorCtaUrl] = useState('');
  const [savingAds, setSavingAds] = useState(false);
  const [adsLoaded, setAdsLoaded] = useState(false);

  const [primaryStream, setPrimaryStream] = useState('');
  const [lowStream, setLowStream] = useState('');
  const [streamsLoaded, setStreamsLoaded] = useState(false);
  const [savingStreams, setSavingStreams] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/menu`).then(r => {
      setItems(r.data.items || {});
      setLoaded(true);
    }).catch(() => setLoaded(true));

    axios.get(`${API}/settings/ads`).then(r => {
      setAdsEnabled(!!r.data.ads_enabled);
      setPublisherId(r.data.ads_publisher_id || '');
      setHomeSlot(r.data.ad_slots?.home || '');
      setScheduleSlot(r.data.ad_slots?.schedule || '');
      setShowsSlot(r.data.ad_slots?.shows || '');
      setFooterSlot(r.data.ad_slots?.footer || '');
      setAdsAfricaDisabled(!!r.data.ads_africa_disabled);
      const sc = r.data.sponsor_card || {};
      setSponsorEnabled(!!sc.enabled);
      setSponsorTitle(sc.title || 'Support Christovibes');
      setSponsorMessage(sc.message || '');
      setSponsorCtaLabel(sc.cta_label || 'Support us');
      setSponsorCtaUrl(sc.cta_url || '');
      setAdsLoaded(true);
    }).catch(() => setAdsLoaded(true));

    axios.get(`${API}/settings/streams`).then(r => {
      setPrimaryStream(r.data.primary_url || '');
      setLowStream(r.data.low_url || '');
      setStreamsLoaded(true);
    }).catch(() => setStreamsLoaded(true));
  }, []);

  const toggle = (key) => {
    setItems(prev => ({ ...prev, [key]: prev[key] === false ? true : false }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/menu`, { items });
      toast.success('Menu settings saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const saveAds = async () => {
    const id = publisherId.trim();
    if (adsEnabled && !PUB_ID_RE.test(id)) {
      return toast.error('Publisher ID must look like ca-pub-XXXXXXXXXXXXXXXX (16 digits)');
    }
    const h = homeSlot.trim();
    const s = scheduleSlot.trim();
    const sh = showsSlot.trim();
    const f = footerSlot.trim();
    const SLOT_RE = /^\d{10}$/;
    if (h && !SLOT_RE.test(h)) return toast.error('Home slot ID must be 10 digits');
    if (s && !SLOT_RE.test(s)) return toast.error('Schedule slot ID must be 10 digits');
    if (sh && !SLOT_RE.test(sh)) return toast.error('Shows slot ID must be 10 digits');
    if (f && !SLOT_RE.test(f)) return toast.error('Footer slot ID must be 10 digits');
    const url = sponsorCtaUrl.trim();
    if (url && !/^(https?:\/\/|\/)/.test(url)) {
      return toast.error('Sponsor CTA URL must start with http(s):// or /');
    }
    setSavingAds(true);
    try {
      await axios.put(`${API}/admin/settings/ads`, {
        ads_enabled: adsEnabled,
        ads_publisher_id: id,
        ads_africa_disabled: adsAfricaDisabled,
        ad_slots: { home: h, schedule: s, shows: sh, footer: f },
        sponsor_card: {
          enabled: sponsorEnabled,
          title: sponsorTitle.trim(),
          message: sponsorMessage.trim(),
          cta_label: sponsorCtaLabel.trim(),
          cta_url: url,
        },
      });
      toast.success(adsEnabled ? 'Ads are ON' : 'Ads are OFF');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save ad settings');
    }
    setSavingAds(false);
  };

  const saveStreams = async () => {
    const p = primaryStream.trim();
    const l = lowStream.trim();
    const HTTP_RE = /^https?:\/\//;
    if (p && !HTTP_RE.test(p)) return toast.error('Primary stream URL must start with http(s)://');
    if (l && !HTTP_RE.test(l)) return toast.error('Low stream URL must start with http(s)://');
    setSavingStreams(true);
    try {
      await axios.put(`${API}/admin/settings/streams`, { primary_url: p, low_url: l });
      toast.success('Stream URLs saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save stream URLs');
    }
    setSavingStreams(false);
  };

  return (
    <div data-testid="admin-settings">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>
          Site Settings
        </h1>
        <p className="text-[#94A3B8] text-sm mt-1">Control what visitors see on the public site</p>
      </div>

      <div className="bg-[#171A21] rounded-2xl border border-white/5 p-6 max-w-lg">
        <h2 className="text-white font-semibold mb-1">Public Menu Items</h2>
        <p className="text-[#94A3B8] text-xs mb-6">Toggle which pages are visible in the public navigation.</p>

        {!loaded ? (
          <div className="flex items-center justify-center py-8"><Loader2 size={24} className="text-[#D4AF37] animate-spin" /></div>
        ) : (
          <div className="space-y-2">
            {MENU_ITEMS.map(item => {
              const enabled = items[item.key] !== false;
              return (
                <div
                  key={item.key}
                  data-testid={`menu-toggle-${item.key}`}
                  className="flex items-center justify-between p-4 rounded-xl border border-white/5 hover:bg-white/[0.02] transition-colors"
                >
                  <span className="text-white text-sm font-medium">{item.label}</span>
                  <button
                    data-testid={`toggle-${item.key}`}
                    onClick={() => toggle(item.key)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? 'bg-[#D4AF37]' : 'bg-white/10'}`}
                  >
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            data-testid="save-menu-settings"
            onClick={save}
            disabled={saving}
            className="bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] rounded-full px-6 py-2.5 flex items-center gap-2 disabled:opacity-50 text-sm"
          >
            {saving ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Google AdSense */}
      <div className="bg-[#171A21] rounded-2xl border border-white/5 p-6 max-w-lg mt-6">
        <h2 className="text-white font-semibold mb-1">Google AdSense</h2>
        <p className="text-[#94A3B8] text-xs mb-6">
          Enable Auto ads once your site is approved by AdSense. The verification meta tag is always present in the site head, so you can request review even while this toggle is off. Ads are automatically consent-gated (non-personalised for "essential only", personalised for "accept all").
        </p>

        {!adsLoaded ? (
          <div className="flex items-center justify-center py-8"><Loader2 size={24} className="text-[#D4AF37] animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Publisher ID</label>
              <input
                data-testid="ads-publisher-input"
                type="text"
                value={publisherId}
                onChange={(e) => setPublisherId(e.target.value)}
                placeholder="ca-pub-XXXXXXXXXXXXXXXX"
                spellCheck={false}
                className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm font-mono focus:outline-none focus:border-[#D4AF37]/40"
              />
              <p className="text-[10px] text-[#94A3B8] mt-1.5">Find yours in AdSense → Account → Settings → Account information.</p>
            </div>

            <div
              data-testid="ads-enable-toggle"
              className="flex items-center justify-between p-4 rounded-xl border border-white/5 hover:bg-white/[0.02] transition-colors"
            >
              <div className="min-w-0">
                <p className="text-white text-sm font-medium">Serve ads to visitors</p>
                <p className="text-[#94A3B8] text-xs mt-0.5">Off until your site is approved by Google.</p>
              </div>
              <button
                data-testid="ads-enable-btn"
                onClick={() => setAdsEnabled(v => !v)}
                className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${adsEnabled ? 'bg-[#D4AF37]' : 'bg-white/10'}`}
              >
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${adsEnabled ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
              </button>
            </div>

            <div className="pt-2 space-y-3">
              <p className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Manual Ad Slots</p>
              <p className="text-[10px] text-[#94A3B8] -mt-1 leading-relaxed">
                Create display ad units in AdSense → <span className="text-white">Ads → By ad unit</span>, copy the 10-digit slot ID and paste it below. Leave blank to hide that placement.
              </p>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-medium">Home page slot (below live player)</label>
                <input
                  data-testid="ads-slot-home"
                  type="text"
                  value={homeSlot}
                  onChange={(e) => setHomeSlot(e.target.value)}
                  placeholder="1234567890"
                  inputMode="numeric"
                  className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm font-mono focus:outline-none focus:border-[#D4AF37]/40"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-medium">Schedule page slot</label>
                <input
                  data-testid="ads-slot-schedule"
                  type="text"
                  value={scheduleSlot}
                  onChange={(e) => setScheduleSlot(e.target.value)}
                  placeholder="1234567890"
                  inputMode="numeric"
                  className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm font-mono focus:outline-none focus:border-[#D4AF37]/40"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-medium">Shows page slot</label>
                <input
                  data-testid="ads-slot-shows"
                  type="text"
                  value={showsSlot}
                  onChange={(e) => setShowsSlot(e.target.value)}
                  placeholder="1234567890"
                  inputMode="numeric"
                  className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm font-mono focus:outline-none focus:border-[#D4AF37]/40"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-medium">Sticky footer slot (all pages)</label>
                <input
                  data-testid="ads-slot-footer"
                  type="text"
                  value={footerSlot}
                  onChange={(e) => setFooterSlot(e.target.value)}
                  placeholder="1234567890"
                  inputMode="numeric"
                  className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm font-mono focus:outline-none focus:border-[#D4AF37]/40"
                />
                <p className="text-[10px] text-[#94A3B8] mt-1.5">Slim persistent banner shown above the audio player on every public route. Listeners can dismiss it per session.</p>
              </div>
            </div>

            <div
              data-testid="ads-africa-toggle"
              className="flex items-center justify-between p-4 rounded-xl border border-white/5 hover:bg-white/[0.02] transition-colors"
            >
              <div className="min-w-0 pr-3">
                <p className="text-white text-sm font-medium">Keep African listeners ad-free</p>
                <p className="text-[#94A3B8] text-xs mt-0.5 leading-relaxed">
                  Suppresses all ads for visitors whose Cloudflare-detected country is in Africa. Requires the site to be served through Cloudflare in production; falls back to showing ads when the geo header isn't set.
                </p>
              </div>
              <button
                data-testid="ads-africa-btn"
                onClick={() => setAdsAfricaDisabled(v => !v)}
                className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${adsAfricaDisabled ? 'bg-[#D4AF37]' : 'bg-white/10'}`}
              >
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${adsAfricaDisabled ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
              </button>
            </div>

            {/* Sponsor Card — shown to African visitors in place of ads */}
            <div className="pt-2 space-y-3">
              <div className="flex items-center justify-between">
                <div className="min-w-0 pr-3">
                  <p className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Sponsor Card</p>
                  <p className="text-[10px] text-[#94A3B8] mt-1 leading-relaxed">
                    Hand-crafted "Support Christovibes" card shown in every ad position (home, schedule, shows, footer) when the visitor is in Africa and the toggle above is on. Leave disabled to just hide the space.
                  </p>
                </div>
                <button
                  data-testid="sponsor-enable-btn"
                  onClick={() => setSponsorEnabled(v => !v)}
                  className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${sponsorEnabled ? 'bg-[#D4AF37]' : 'bg-white/10'}`}
                >
                  <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${sponsorEnabled ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-medium">Title</label>
                <input
                  data-testid="sponsor-title"
                  type="text"
                  value={sponsorTitle}
                  onChange={(e) => setSponsorTitle(e.target.value)}
                  maxLength={80}
                  className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-medium">Message</label>
                <textarea
                  data-testid="sponsor-message"
                  rows={3}
                  value={sponsorMessage}
                  onChange={(e) => setSponsorMessage(e.target.value)}
                  maxLength={400}
                  placeholder="Warm one-liner explaining how listeners can support the station."
                  className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40 resize-none"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-medium">CTA label</label>
                  <input
                    data-testid="sponsor-cta-label"
                    type="text"
                    value={sponsorCtaLabel}
                    onChange={(e) => setSponsorCtaLabel(e.target.value)}
                    maxLength={40}
                    className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-medium">CTA URL</label>
                  <input
                    data-testid="sponsor-cta-url"
                    type="url"
                    value={sponsorCtaUrl}
                    onChange={(e) => setSponsorCtaUrl(e.target.value)}
                    placeholder="https://… or /page"
                    className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                data-testid="save-ads-settings"
                onClick={saveAds}
                disabled={savingAds}
                className="bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] rounded-full px-6 py-2.5 flex items-center gap-2 disabled:opacity-50 text-sm"
              >
                {savingAds ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : 'Save AdSense'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Radio Streams */}
      <div className="bg-[#171A21] rounded-2xl border border-white/5 p-6 max-w-lg mt-6">
        <h2 className="text-white font-semibold mb-1">Radio Streams</h2>
        <p className="text-[#94A3B8] text-xs mb-6">
          Point the mobile app at your Icecast/RadioBOSS streams. The mobile client automatically switches to the leaner low-bitrate URL when the listener is on a slow cellular connection (2G/3G or metered network). Leave the low-bitrate field empty to always use the primary stream.
        </p>

        {!streamsLoaded ? (
          <div className="flex items-center justify-center py-8"><Loader2 size={24} className="text-[#D4AF37] animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Primary stream URL</label>
              <input
                data-testid="stream-primary"
                type="url"
                value={primaryStream}
                onChange={(e) => setPrimaryStream(e.target.value)}
                placeholder="https://…/stream"
                spellCheck={false}
                className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm font-mono focus:outline-none focus:border-[#D4AF37]/40"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Low-bitrate stream URL (optional)</label>
              <input
                data-testid="stream-low"
                type="url"
                value={lowStream}
                onChange={(e) => setLowStream(e.target.value)}
                placeholder="https://…/stream-low"
                spellCheck={false}
                className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm font-mono focus:outline-none focus:border-[#D4AF37]/40"
              />
              <p className="text-[10px] text-[#94A3B8] mt-1.5">Typically a 48–64 kbps variant of your primary stream.</p>
            </div>
            <div className="flex justify-end">
              <button
                data-testid="save-streams"
                onClick={saveStreams}
                disabled={savingStreams}
                className="bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] rounded-full px-6 py-2.5 flex items-center gap-2 disabled:opacity-50 text-sm"
              >
                {savingStreams ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : 'Save Streams'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
